import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowPathIcon, BookOpenIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/common/PageHeader';
import { ROUTES } from '@/constants/routes';
import { useTranslation } from '@/store/languageStore';
import { usePermissionsStore } from '@/store/permissionsStore';
import { useAuthStore } from '@/store/authStore';
import api from '@/services/api/client';
import { ObdKbMarkdown } from '@/components/admin/ObdKbMarkdown';

interface KbSource {
  id: number;
  wikiTitle: string;
  pageUrl: string;
  section: string;
  lastRevid: number | null;
  lastCheckedAt: string | null;
  lastChangedAt: string | null;
  hasPage: boolean;
}

interface KbHit {
  sourceId: number;
  title: string;
  snippet: string;
  pageUrl: string;
  section: string;
}

interface KbPage {
  sourceId: number;
  title: string;
  markdown: string;
  pageUrl: string;
  fetchedAt: string;
  section?: string;
}

interface VehicleRow {
  id: number;
  make: string;
  model: string;
  modelYear: string | null;
  fuelType?: string | null;
  parameters: Record<string, boolean>;
}

function paramLabel(key: string): string {
  return key.replace(/_/g, ' ');
}

function isVehicleListPage(page: { title: string; section?: string }): boolean {
  return page.section === 'vehicle_list' || /supported vehicle list/i.test(page.title);
}

const ObdKnowledgePage: React.FC = () => {
  const { t } = useTranslation();
  const canSync =
    useAuthStore((s) => s.user?.is_global_admin === true) ||
    usePermissionsStore((s) => s.hasActionAccess('ADMIN_OBD_KNOWLEDGE', 'SYNC'));

  const [sources, setSources] = useState<KbSource[]>([]);
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<KbHit[]>([]);
  const [vehicles, setVehicles] = useState<VehicleRow[]>([]);
  const [vehicleTotal, setVehicleTotal] = useState(0);
  const [parameterKeys, setParameterKeys] = useState<string[]>([]);
  const [page, setPage] = useState<KbPage | null>(null);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState<string | null>(null);
  const [citations, setCitations] = useState<Array<{ title: string; pageUrl: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [asking, setAsking] = useState(false);

  const loadSources = useCallback(async () => {
    try {
      const response = await api.get('/api/admin/obd-knowledge/sources');
      setSources(response.data?.data ?? []);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || t('obdKnowledge.loadFailed'));
    }
  }, [t]);

  useEffect(() => {
    void loadSources();
  }, [loadSources]);

  const handleSearch = async () => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      const response = await api.get('/api/admin/obd-knowledge/search', { params: { q: query.trim() } });
      const payload = response.data?.data;
      setHits(Array.isArray(payload) ? payload : payload?.hits ?? []);
      setVehicles(payload?.vehicles?.rows ?? []);
      setVehicleTotal(payload?.vehicles?.total ?? 0);
      setParameterKeys(payload?.vehicles?.parameterKeys ?? []);
      setPage(null);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || t('obdKnowledge.searchFailed'));
    } finally {
      setLoading(false);
    }
  };

  const openSource = async (sourceId: number) => {
    try {
      const response = await api.get(`/api/admin/obd-knowledge/pages/${sourceId}`);
      setPage(response.data?.data ?? null);
    } catch {
      toast.error(t('obdKnowledge.pageMissing'));
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await api.post('/api/admin/obd-knowledge/sync', { force: false }, { timeout: 180000 });
      const result = response.data?.data;
      toast.success(t('obdKnowledge.synced', { updated: result?.updated ?? 0, checked: result?.checked ?? 0 }));
      await loadSources();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || t('obdKnowledge.syncFailed'));
    } finally {
      setSyncing(false);
    }
  };

  const handleAsk = async () => {
    if (!question.trim()) return;
    setAsking(true);
    setAnswer(null);
    try {
      const response = await api.post('/api/admin/obd-knowledge/ask', { question: question.trim() }, { timeout: 120000 });
      setAnswer(response.data?.data?.answer ?? '');
      setCitations(response.data?.data?.citations ?? []);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string; code?: string } } };
      toast.error(err.response?.data?.message || t('obdKnowledge.askFailed'));
    } finally {
      setAsking(false);
    }
  };

  const vehicleListHref = `${ROUTES.ADMIN_OBD_VEHICLES}${query.trim() ? `?make=${encodeURIComponent(query.trim())}` : ''}`;

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title={t('obdKnowledge.title')}
        description={t('obdKnowledge.description')}
        icon={<BookOpenIcon className="w-6 h-6 text-primary" />}
        backTo="/system/administration"
        actions={
          canSync ? (
            <button
              type="button"
              onClick={() => void handleSync()}
              disabled={syncing}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-primary text-white text-sm"
            >
              <ArrowPathIcon className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
              {t('obdKnowledge.sync')}
            </button>
          ) : undefined
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-3">
          <h2 className="font-medium">{t('obdKnowledge.sources')}</h2>
          <ul className="space-y-2 text-sm">
            {sources.map((source) => (
              <li key={source.id} className="rounded-lg border border-border p-3">
                <button type="button" className="text-left w-full" onClick={() => void openSource(source.id)}>
                  <div className="font-medium">{source.wikiTitle}</div>
                  <div className="text-textTertiary text-xs">
                    {source.section}
                    {source.lastChangedAt ? ` · ${new Date(source.lastChangedAt).toLocaleString('sv-SE')}` : ''}
                    {!source.hasPage ? ` · ${t('obdKnowledge.notFetched')}` : ''}
                  </div>
                </button>
                <a href={source.pageUrl} target="_blank" rel="noreferrer" className="text-primary text-xs underline">
                  {t('obdKnowledge.openWiki')}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-2 space-y-4">
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              void handleSearch();
            }}
          >
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('obdKnowledge.searchPlaceholder')}
              className="flex-1 rounded-lg border border-border bg-bgContainer px-3 py-2"
            />
            <button type="submit" className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-primary text-white">
              <MagnifyingGlassIcon className="w-4 h-4" />
              {t('common.search')}
            </button>
          </form>

          {loading && <p className="text-sm">{t('common.loading')}</p>}

          {!loading && query.trim() && vehicleTotal === 0 && (
            <p className="text-sm text-textSecondary">
              {t('obdKnowledge.noVehicleHits')}{' '}
              <Link to={vehicleListHref} className="text-primary underline">
                {t('obdKnowledge.openVehicleList')}
              </Link>
            </p>
          )}

          {vehicleTotal > 0 && (
            <div className="rounded-xl border border-border overflow-hidden">
              <div className="flex justify-between items-center px-3 py-2 bg-bgSecondary text-textLight">
                <h2 className="font-medium text-sm">{t('obdKnowledge.vehicleHits', { count: vehicleTotal })}</h2>
                <Link to={vehicleListHref} className="text-textLight text-xs underline">
                  {t('obdKnowledge.openVehicleList')}
                </Link>
              </div>
              <div className="overflow-x-auto max-w-full overscroll-x-contain">
                <table className="w-max min-w-full text-sm">
                  <thead className="text-left">
                    <tr>
                      <th className="px-3 py-2 whitespace-nowrap">{t('obdVehicles.make')}</th>
                      <th className="px-3 py-2 whitespace-nowrap">{t('obdVehicles.model')}</th>
                      <th className="px-3 py-2 whitespace-nowrap">{t('obdVehicles.year')}</th>
                      <th className="px-3 py-2 whitespace-nowrap">{t('obdVehicles.fuelType')}</th>
                      {parameterKeys.map((key) => (
                        <th key={key} className="px-3 py-2 whitespace-nowrap">
                          {paramLabel(key)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {vehicles.map((row) => (
                      <tr key={row.id} className="border-t border-border">
                        <td className="px-3 py-2 whitespace-nowrap">{row.make}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{row.model}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{row.modelYear ?? '–'}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{row.fuelType ?? '–'}</td>
                        {parameterKeys.map((key) => (
                          <td key={key} className="px-3 py-2 text-center">
                            {row.parameters[key] ? '+' : ''}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {hits.map((hit) => (
            <button
              key={`${hit.sourceId}-${hit.title}`}
              type="button"
              onClick={() => void openSource(hit.sourceId)}
              className="block w-full text-left rounded-lg border border-border p-3"
            >
              <div className="font-medium">{hit.title}</div>
              <p className="text-sm text-textSecondary mt-1">{hit.snippet}</p>
            </button>
          ))}

          {page && (
            <article className="rounded-xl border border-border p-4 space-y-2">
              <div className="flex justify-between gap-3">
                <h2 className="font-semibold">{page.title}</h2>
                <a href={page.pageUrl} target="_blank" rel="noreferrer" className="text-primary text-sm underline">
                  {t('obdKnowledge.openWiki')}
                </a>
              </div>
              {isVehicleListPage(page) ? (
                <div className="text-sm space-y-2">
                  <p className="text-textSecondary">{t('obdKnowledge.vehicleListPageNote')}</p>
                  <Link to={vehicleListHref} className="text-primary underline">
                    {t('obdKnowledge.openVehicleList')}
                  </Link>
                </div>
              ) : (
                <ObdKbMarkdown markdown={page.markdown} />
              )}
            </article>
          )}

          <div className="rounded-xl border border-border p-4 space-y-3">
            <h2 className="font-medium">{t('obdKnowledge.askAi')}</h2>
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              rows={3}
              placeholder={t('obdKnowledge.askPlaceholder')}
              className="w-full rounded-lg border border-border bg-bgContainer px-3 py-2"
            />
            <button
              type="button"
              onClick={() => void handleAsk()}
              disabled={asking}
              className="px-3 py-2 rounded-lg bg-primary text-white text-sm"
            >
              {asking ? t('common.loading') : t('obdKnowledge.ask')}
            </button>
            {answer && (
              <div className="text-sm space-y-2">
                <ObdKbMarkdown markdown={answer} />
                {citations.length > 0 && (
                  <ul className="text-xs text-textTertiary">
                    {citations.map((cite) => (
                      <li key={cite.pageUrl}>
                        <a href={cite.pageUrl} target="_blank" rel="noreferrer" className="underline">
                          {cite.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ObdKnowledgePage;
